import { Vector3, MathUtils } from 'three';

import { Renderer } from './Renderer.js';
import { Time } from './Time.js';
import { CameraRig } from './CameraRig.js';
import { frame } from './FrameUniforms.js';

import { Environment } from '../world/Environment.js';
import { Ground } from '../world/Ground.js';
import { DustMotes } from '../world/DustMotes.js';
import { ContactShadows } from '../world/ContactShadows.js';

import { AssetLoader } from '../loaders/AssetLoader.js';
import { CharacterController } from '../animation/CharacterController.js';

import { InputManager } from '../input/InputManager.js';
import { AimController } from '../input/AimController.js';

import { ParticleEngine } from '../particles/ParticleEngine.js';
import { LightPool } from '../effects/LightPool.js';
import { DecalSystem, DecalType } from '../effects/GroundDecals.js';
import { FissureSystem } from '../effects/GroundFissures.js';
import { BurstSystem, BurstMode } from '../effects/BurstSphere.js';
import { CameraShake } from '../effects/CameraShake.js';
import { ScreenFlash } from '../effects/ScreenFlash.js';

import { AbilityManager } from '../abilities/AbilityManager.js';
import { PostProcessing } from '../postprocessing/PostProcessing.js';

import { HUD, LoadingScreen } from '../ui/HUD.js';
import { Editor } from '../ui/Editor.js';

import { settings, ELEMENTS } from '../config/settings.js';

const HDR_URL = './hdri/spruit_sunrise.hdr';

/**
 * Application root: owns every subsystem and the frame loop.
 *
 * The wiring is deliberately one-directional — App builds the systems, hands the
 * ability manager a context object of the shared services, and then does nothing
 * but order the per-frame updates. No subsystem reaches back into App.
 *
 * The interaction is a single loop: select and arm an ability (Q / E), swing the
 * ground arrow with the mouse, click to fire. `AimController` owns the targeting
 * and emits one `cast` event; App turns that into an ability, a heading for the
 * character and a cooldown.
 */
export class App {
  constructor(canvas) {
    this.canvas = canvas;
    this.time = new Time();
    this.elapsed = 0;
    this.paused = false;
    this._raf = 0;
    this.quickAbilities = [...ELEMENTS.slice(0, 6), ELEMENTS[6]];
    this.chargingAbility = null;

    /**
     * Seconds left before each ability can be armed again. Per element, so
     * spending one slot never locks the other out.
     */
    this.cooldowns = new Map(ELEMENTS.map((element) => [element, 0]));

    /* ---- core ---- */
    this.renderer = new Renderer(canvas);
    this.rig = new CameraRig(canvas);
    this.camera = this.rig.camera;

    this.environment = new Environment(this.renderer, this.camera);
    this.scene = this.environment.scene;

    /* ---- world ---- */
    this.ground = new Ground(this.environment);
    this.dust = new DustMotes();
    this.contactShadows = new ContactShadows(this.renderer, { size: 2.6, height: 2.4, blur: 2.0 });

    this.scene.add(this.ground.mesh, this.dust.points, this.contactShadows.group);
    this.dust.setPixelRatio(this.renderer.gl.getPixelRatio());

    /* ---- shared VFX services ---- */
    this.particles = new ParticleEngine(this.scene);
    this.lights = new LightPool(this.scene);
    this.decals = new DecalSystem(this.scene);
    this.fissures = new FissureSystem(this.scene);
    this.bursts = new BurstSystem(this.scene);
    this.shake = new CameraShake(this.rig);
    this.flash = new ScreenFlash();

    // Worst-case simultaneous resources used by four overlapping casts from
    // the new Spellbook set. Constructors and shader variants therefore stay
    // behind the loader instead of the first T press.
    // Bramble may leave twelve pod impacts plus its centre mark per cast.
    this.decals.prewarm(DecalType.CRACK, 52);
    this.bursts.prewarm(BurstMode.EARTH, 4);
    this.decals.prewarm(DecalType.SHOCKWAVE, 4);
    this.decals.prewarm(DecalType.SCORCH, 4);
    this.bursts.prewarm(BurstMode.FIRE, 8);
    this.bursts.prewarm(BurstMode.STORM, 8);
    this.fissures.prewarm(4);

    this.abilities = new AbilityManager({
      scene: this.scene,
      camera: this.camera,
      environment: this.environment,
      particles: this.particles,
      lights: this.lights,
      decals: this.decals,
      fissures: this.fissures,
      bursts: this.bursts,
      shake: this.shake,
      flash: this.flash,
      onCooldownStart: (element, duration) => this.startCooldown(element, duration)
    });

    /* ---- character ---- */
    this.character = new CharacterController(this.environment);
    this.scene.add(this.character.root);
    // Ability instances were structurally prewarmed above. They all retain the
    // same context object, so wiring the gameplay host here does not reconstruct
    // a pool entry or move work onto the first cast.
    this.abilities.ctx.character = this.character;

    /* ---- input & targeting ---- */
    this.input = new InputManager(canvas);
    this.aim = new AimController(this.camera);
    this.scene.add(this.aim.object3D);

    /* ---- post ---- */
    this.post = new PostProcessing(this.renderer, this.scene, this.camera);

    /* ---- UI ---- */
    this.loading = new LoadingScreen();
    this.hud = new HUD(document.getElementById('hud'));
    this.editor = new Editor({
      onClear: () => this.clearEffects(),
      onToast: (message) => this.hud.showToast(message)
    });

    this._bindEvents();
    this.selectAbility(ELEMENTS[0], { silent: true });

    this._focusPoint = new Vector3();
  }

  /** The ability currently in the slot. */
  get element() {
    return this.abilities.selected;
  }

  /* ------------------------------------------------------------------ */

  _bindEvents() {
    this.renderer.onResize((width, height, pixelRatio) => {
      this.rig.resize(width, height);
      this.post.setSize(width, height, pixelRatio);
      this.dust.setPixelRatio(pixelRatio);
    });

    this.input.on('pointer:move', (pointer) => this.aim.point(pointer));
    this.input.on('pointer:confirm', (pointer) => {
      this.aim.point(pointer);
      if (this.chargingAbility) return;
      this.aim.confirm();
    });
    this.input.on('action', (action, slot) => this._handleAction(action, slot));

    this.aim.on('cast', (origin, direction, distance) => this._cast(origin, direction, distance));
    this.aim.on('reject', () => {
      if (this.chargingAbility) {
        this._cancelCharge();
        this.aim.cancel();
      }
      this.hud.showToast('Too close — aim further out');
    });

    this.hud.onAbility = (element) => this.armAbility(element);
    this.hud.onAbilityRelease = (element) => this.releaseAbility(element);
    this.hud.onSpellbookSelect = (element) => this.equipSpellbookAbility(element);
  }

  _handleAction(action, slot) {
    switch (action) {
      case 'ability': {
        const element = this.quickAbilities[slot] ?? this.element;
        if (settings[element]?.holdToCast) {
          this.armAbility(element);
          break;
        }
        // Pressing the *same* key again puts an armed cast away, as it does in a
        // MOBA; pressing a different one swaps the slot without disarming.
        if (this.aim.isArmed && element === this.element) this.aim.cancel();
        else this.armAbility(element);
        break;
      }
      case 'abilityRelease': {
        const element = this.quickAbilities[slot] ?? this.element;
        this.releaseAbility(element);
        break;
      }
      case 'cancel':
        if (this.hud.closeSpellbook()) break;
        this._cancelCharge();
        this.aim.cancel();
        break;
      case 'toggleHelp':
        this.hud.toggleHelp();
        break;
      case 'toggleEditor':
        this.editor.toggle();
        break;
      case 'clear':
        this.clearEffects();
        this.hud.showToast('Effects cleared');
        break;
      case 'togglePause':
        this.paused = !this.paused;
        this.hud.setPaused(this.paused);
        this.hud.showToast(this.paused ? 'Paused — the editor still applies' : 'Resumed');
        break;
      default:
        break;
    }
  }

  /**
   * Put an ability in the slot. The aim indicator and the HUD both follow,
   * because `range` and `minRange` are the ability's, not the app's.
   */
  selectAbility(element, options = {}) {
    if (!ELEMENTS.includes(element)) return;
    this.abilities.select(element);
    this.aim.setElement(element);
    this.hud.setElement(element, options);
  }

  /** Equip a spell from the book into the seventh, T-bound quick slot. */
  equipSpellbookAbility(element) {
    if (!ELEMENTS.includes(element)) return;
    this._cancelCharge();
    this.aim.cancel();
    this.quickAbilities[6] = element;
    this.hud.setQuickAbility(element);
    this.selectAbility(element);
  }

  /** Select an ability and arm it, unless it is still cooling down. */
  armAbility(element = this.element) {
    if (this.chargingAbility) return;
    const existing = settings[element]?.singleActive ? this.abilities.getActive(element) : null;
    if (existing) {
      if (existing.canReactivate) {
        this.aim.cancel();
        const reactivated = this.abilities.reactivate(element);
        if (reactivated) {
          this.character.playCast(settings[element].castAnim);
          this.character.castLunge();
          this.character.castGesture(settings[element].castGesture);
          this.hud.showToast('Troca Sombria');
        }
      } else {
        this.hud.showToast('Passo Sombrio em andamento');
      }
      return;
    }
    if ((this.cooldowns.get(element) ?? 0) > 0) {
      this.hud.showToast('Not ready');
      return;
    }
    // Selecting before arming means the arrow is already drawn to the new
    // ability's range on the frame it appears.
    if (element !== this.element) this.selectAbility(element);
    this.aim.arm();
    if (settings[element]?.holdToCast) {
      this.chargingAbility = this.abilities.beginCharge(this.character.position, element);
      if (!this.chargingAbility) this.aim.cancel();
    }
  }

  releaseAbility(element = this.element) {
    if (!this.chargingAbility || this.chargingAbility.element !== element) return;
    this.aim.confirm();
  }

  _cancelCharge() {
    if (!this.chargingAbility) return;
    this.abilities.cancelCharge(this.chargingAbility);
    this.chargingAbility = null;
    this.hud.setCharge(null, 0);
  }

  _cast(origin, direction, distance) {
    const element = this.element;
    if (this.chargingAbility?.element === element) {
      this.abilities.releaseCharge(this.chargingAbility, origin, direction, distance);
      this.chargingAbility = null;
      this.hud.setCharge(null, 0);
    } else {
      this.abilities.cast(origin, direction, distance, element);
    }
    if (!settings[element].deferCooldown) this.startCooldown(element);

    // Snap onto the shot and throw the body into it. Which clip that is belongs
    // to the ability, so each spell can be cast with its own gesture.
    this.character.setFacing(this.aim.facing);
    this.character.playCast(settings[element].castAnim);
    this.character.castLunge();
    this.character.castGesture(settings[element].castGesture);
  }

  startCooldown(element, duration = settings[element]?.cooldown ?? 0) {
    if (!this.cooldowns.has(element)) return;
    this.cooldowns.set(element, Math.max(this.cooldowns.get(element) ?? 0, Math.max(0, duration)));
  }

  clearEffects() {
    this._cancelCharge();
    this.aim.cancel();
    this.abilities.clear();
    this.particles.reset();
    this.decals.clear();
    this.fissures.clear();
    this.bursts.clear();
    this.lights.reset();
    this.shake.reset();
    this.flash.reset();
  }

  /* ------------------------------------------------------------------ */

  /** Load assets, warm the shader cache, then start the loop. */
  async load() {
    const assets = new AssetLoader();

    this.loading.setProgress(0.05, 'Loading environment…');
    const hdr = await assets.loadHDR(HDR_URL);
    await this.environment.loadEnvironment(hdr);
    frame.uEnvMap.value = this.environment.equirect;

    this.loading.setProgress(0.35, 'Loading floor…');
    await this.ground.loadTextures(assets);

    this.loading.setProgress(0.5, 'Loading character…');
    await this.character.load(assets);

    this.loading.setProgress(0.85, 'Compiling shaders…');
    // Compile everything up front so the first cast never stutters. Pooled
    // abilities are normally hidden, so expose one instance of every shader
    // variant only for the duration of this loading pass.
    const restoreWarmup = this.abilities.exposeForShaderWarmup();
    try {
      this.aim.indicator.setVisible(true);
      this.aim.zone.setVisible(true);
      const offscreen = this._focusPoint.set(0, -999, 0);
      this.decals.spawn(DecalType.CRACK, offscreen, { radius: 1, life: 0.01 });
      this.decals.spawn(DecalType.SHOCKWAVE, offscreen, { radius: 1, life: 0.01 });
      this.decals.spawn(DecalType.SCORCH, offscreen, { radius: 1, life: 0.01 });
      this.bursts.spawn(BurstMode.EARTH, offscreen, { radius: 1, endRadius: 1, life: 0.01 });
      this.bursts.spawn(BurstMode.FIRE, offscreen, { radius: 1, endRadius: 1, life: 0.01 });
      this.bursts.spawn(BurstMode.STORM, offscreen, { radius: 1, endRadius: 1, life: 0.01 });
      this.fissures.spawn(offscreen, { radius: 1, life: 0.01 });
      await this.renderer.gl.compileAsync(this.scene, this.camera);
    } finally {
      restoreWarmup();
      this.aim.indicator.setVisible(false);
      this.aim.zone.setVisible(false);
      this.decals.clear();
      this.bursts.clear();
      this.fissures.clear();
    }

    this.loading.setProgress(1, 'Ready');
    this.loading.hide();

    this.start();
  }

  start() {
    this.time.reset();
    const loop = () => {
      this._raf = requestAnimationFrame(loop);
      this.frame();
    };
    this._raf = requestAnimationFrame(loop);
  }

  stop() {
    cancelAnimationFrame(this._raf);
  }

  /* ------------------------------------------------------------------ */

  frame() {
    const gl = this.renderer.gl;
    gl.info.reset();

    const raw = this.time.tick();
    const dt = this.paused ? 0 : raw * settings.global.timeScale;
    this.elapsed += dt;

    /* ---- shared uniforms ---- */
    frame.uTime.value = this.elapsed;
    frame.uDelta.value = dt;
    frame.uShaderIntensity.value = settings.global.shaderIntensity;
    frame.uGlobalGlow.value = settings.global.glow;
    frame.uCameraNear.value = this.camera.near;
    frame.uCameraFar.value = this.camera.far;

    /* ---- simulation ---- */
    this.renderer.syncSettings();

    this.environment.setFocus(this.character.position.x, this.character.position.z);
    this.environment.update();

    // Targeting runs on *real* time so the arrow keeps sweeping and animating
    // while the sandbox is paused — pausing freezes the effects, not the UI.
    this.aim.setOrigin(this.character.position);
    this.aim.update(raw);

    if (settings.character.turnToAim && this.aim.isArmed) {
      this.character.turnToward(this.aim.facing, settings.character.turnRate, raw);
    }
    this.character.update(dt);

    for (const [element, remaining] of this.cooldowns) {
      if (remaining > 0) this.cooldowns.set(element, Math.max(0, remaining - raw));
    }

    this.ground.update(this.elapsed);
    this.dust.update(this.elapsed, this.character.position);

    this.chargingAbility?.syncChargeOrigin(this.character.position);
    this.abilities.update(dt);
    this.particles.flush();
    this.decals.update(dt);
    this.fissures.update(dt);
    this.bursts.update(dt);
    this.lights.update(dt);

    /* ---- camera ---- */
    const focus = this.abilities.focus;
    if (focus) this.rig.lookAt(focus.position, MathUtils.clamp(1 - focus.u * 0.4, 0, 1));
    this.rig.setAnchor(this.character.position.x, 0, this.character.position.z);
    this.shake.update(raw);
    this.flash.update(raw);
    this.rig.update(raw);

    this.contactShadows.setPosition(this.character.position.x, this.character.position.z);
    this.contactShadows.render(this.scene);

    /* ---- render ---- */
    // Exactly one cascade shadow update per frame (see Renderer).
    gl.shadowMap.needsUpdate = true;
    this.post.sync(this.elapsed, this.flash);
    this.post.render();

    /* ---- readouts ---- */
    for (const element of ELEMENTS) {
      this.hud.setCooldown(element, this.cooldowns.get(element) ?? 0, settings[element].cooldown);
    }
    const quick = this.quickAbilities[6];
    const reactivation = this.abilities.getActive(quick);
    this.hud.setReactivation(
      quick,
      Boolean(reactivation?.canReactivate),
      reactivation?.reactivationRatio ?? 0
    );
    this.hud.setArmed(this.aim.isArmed);
    if (this.chargingAbility) {
      this.hud.setCharge(this.chargingAbility.element, this.chargingAbility.charge);
    }
    this.hud.update(raw, () => ({
      particles: this.particles.countLive(this.elapsed),
      calls: gl.info.render.calls,
      spikes: this.abilities.active.reduce((total, ability) => total + ability.instanceCount, 0),
      abilities: this.abilities.active.length
    }));
  }

  /* ------------------------------------------------------------------ */

  dispose() {
    this.stop();
    this.input.dispose();
    this.aim.dispose();
    this.abilities.dispose();
    this.particles.dispose();
    this.decals.dispose();
    this.fissures.dispose();
    this.bursts.dispose();
    this.lights.dispose();
    this.character.dispose();
    this.ground.dispose();
    this.dust.dispose();
    this.contactShadows.dispose();
    this.post.dispose();
    this.environment.dispose();
    this.editor.dispose();
    this.rig.dispose();
    this.renderer.dispose();
  }
}
